/**
 * Content-driven SEO derivation helpers (task #24).
 *
 * These produce a distinct <title>, a meaningful meta description, and a single
 * <h1> for every page WITHOUT hardcoding per-slug strings. Everything is derived
 * from the page document the CMS already ships:
 *   - title:       page.title (+ a natural qualifier for commerce pages)
 *   - description: page.description, else the first real sentence(s) of body copy
 *   - h1:          the page title (the template renders exactly one)
 *
 * Region-safe: none of this reads or injects the region prefix, so a region page
 * stays self-canonical and its visible/meta copy is identical across /us|/eu|/au.
 */

// Section _type values that mean "this is a product checkout / buy page" — used
// to add a short qualifier so a product checkout page ("Pro Pedals") gets a
// title distinct from the matching hardware overview page ("Pro Pedals").
const CHECKOUT_SECTION_TYPES = new Set([
  'productCheckoutPage',
  'productOffer',
  'gt2CockpitCheckoutPage',
  'gt2FullRigCheckoutPage',
  'upgradableWheelBaseCheckoutPage',
  'uDFP20UpgradesCheckoutPage',
])

const isCheckoutPage = (content = [], tag) =>
  (typeof tag === 'string' && tag.toLowerCase() === 'checkout') ||
  content.some((s) => s && CHECKOUT_SECTION_TYPES.has(s._type))

// Pull plain text out of a value that may be a string, a Sanity portable-text
// block array, a highlight object ({ text } / { children }) or nested content.
// Returns '' when nothing usable is found. Bounded recursion keeps it cheap on
// the SSR hot path.
const extractText = (value, depth = 0) => {
  if (value == null || depth > 6) return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    return value.map((v) => extractText(v, depth + 1)).filter(Boolean).join(' ')
  }
  if (typeof value === 'object') {
    // Portable-text span / block
    if (typeof value.text === 'string') return value.text
    if (Array.isArray(value.children)) return extractText(value.children, depth + 1)
    return ''
  }
  return ''
}

// An explicit, human-written description/excerpt on the section (or on a
// referenced sub-document such as a blogPost) is the BEST source — prefer it
// verbatim over walking body prose.
const EXPLICIT_DESC_FIELDS = [
  'description',
  'excerpt',
  'metaDescription',
  'seoDescription',
  // Curated checkout/marketing copy: productCheckoutPage + uDFP20UpgradesCheckoutPage
  // hold it in `overrideDescription`; upgradableWheelBaseCheckoutPage in `offersText`.
  // Without these, ~17 product pages' hand-written copy was thrown away → default.
  'overrideDescription',
  'offersText',
]

// Referenced sub-documents that the GROQ query dereferences at runtime and that
// carry their own copy (blog articles, legal docs). The page section itself is
// just a thin wrapper around these.
const REFERENCED_DOC_FIELDS = ['blogPost', 'legalDocument']

// Fields, in priority order, that tend to hold the lead/intro copy of a section.
// We look at the section's own body text first, then sub-title-ish fields.
const BODY_TEXT_FIELDS = [
  'text',
  'description',
  'body',
  'subtitle',
  'subtitleHl',
  'paragraph',
  'content',
  // Hero/intro copy on landing pages (creators program, NASCAR team, etc.).
  'heroText',
  'heroSubtitle',
  'stepsDescription',
]

// Container fields whose ITEMS are themselves sections carrying lead copy (e.g.
// aboutUsPage nests its real prose inside `sections[]`). We descend exactly one
// level so that copy is reachable without an unbounded walk. ('content' is
// intentionally excluded — it's already scanned as a body text field above.)
const CONTAINER_FIELDS = ['sections', 'pages', 'cards']

// Collapse whitespace and strip price/template placeholders (`${...}`) that are
// only meaningful once client-side prices hydrate — they make ugly meta copy.
const clean = (s) =>
  String(s || '')
    .replace(/\$\{[^}]*\}/g, '')
    .replace(/\s+/g, ' ')
    .trim()

// Trim to a meta-description-friendly length on a word boundary (~160 chars).
const truncate = (s, max = 160) => {
  if (s.length <= max) return s
  const cut = s.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  return (lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trim() + '…'
}

// Synthesize a spec-sheet meta description that is DISTINCT per product AND per
// product CATEGORY (Audit C M4). The old code emitted ONE identical template
// sentence for all 7 specsPage pages (only the product name varied) → near-
// duplicate descriptions. The spec sheet's `itemType` tells us whether the
// product is a wheel base, pedal set or steering wheel, so each page names the
// spec highlights that actually matter for that category. Ordering matters:
// WHEEL_BASE contains "WHEEL", so it must be tested before STEERING_WHEEL.
const specSheetDescription = (product, itemType = '') => {
  const t = String(itemType).toUpperCase()
  if (/WHEEL_BASE|TORQUE/.test(t)) {
    return `${product} technical specs: peak torque, force-feedback fidelity, connectivity and wheel-mount dimensions for this VRS direct-drive wheel base.`
  }
  if (/PEDAL/.test(t)) {
    return `${product} technical specs: pedal travel, load-cell brake force, adjustment range and mounting dimensions for these VRS sim racing pedals.`
  }
  if (/STEERING_WHEEL/.test(t)) {
    return `${product} technical specs: rim diameter, materials, button and encoder layout, display and connectivity for this VRS sim racing wheel.`
  }
  return `${product} technical specs: dimensions, connectivity and performance data for this VRS DirectForce Pro component.`
}

/**
 * Derive a meta description from the page's real body copy.
 * Returns '' when no usable copy exists (caller falls back to the global default).
 */
export const deriveDescription = (content = [], { title } = {}) => {
  if (!Array.isArray(content)) return ''

  // Spec sheets carry no prose (only an `itemType`), so they always fell through
  // to the global default.
  const specsSection = content.find((s) => s && s._type === 'specsPage')
  if (title && specsSection) {
    // 1) Prefer authored copy: if an editor has added an explicit description on
    //    ANY section of the specs page, honour it verbatim (best SEO copy). This
    //    runs before the synthesized template so authored copy always wins.
    for (const section of content) {
      if (!section || typeof section !== 'object') continue
      for (const field of EXPLICIT_DESC_FIELDS) {
        const raw = clean(extractText(section[field]))
        if (raw && raw.split(' ').length >= 4) {
          return truncate(raw)
        }
      }
    }

    // 2) Otherwise synthesize a description that is distinct per product AND per
    //    category (from the spec sheet's itemType) instead of one identical
    //    sentence for all 7 spec pages (Audit C M4).
    const product = clean(title)
      .replace(/\s*[-–]\s*Technical Specifications.*$/i, '')
      .trim()
    if (product) {
      return truncate(specSheetDescription(product, specsSection.itemType))
    }
  }

  // For each section, the candidate source is the section itself, any referenced
  // sub-document (blogPost / legalDocument) it wraps, and one level of nested
  // container sections (e.g. aboutUsPage keeps its prose inside `sections[]`).
  for (const section of content) {
    if (!section || typeof section !== 'object') continue
    const candidates = [section]
    for (const refField of REFERENCED_DOC_FIELDS) {
      if (section[refField] && typeof section[refField] === 'object') {
        candidates.push(section[refField])
      }
    }
    for (const cf of CONTAINER_FIELDS) {
      if (Array.isArray(section[cf])) {
        for (const sub of section[cf]) {
          if (sub && typeof sub === 'object') candidates.push(sub)
        }
      }
    }

    // 1) Prefer an explicit, human-written description/excerpt (best SEO copy).
    for (const node of candidates) {
      for (const field of EXPLICIT_DESC_FIELDS) {
        const raw = clean(extractText(node[field]))
        if (raw && raw.split(' ').length >= 4) {
          return truncate(raw)
        }
      }
    }

    // 2) Otherwise derive from the lead body copy.
    for (const node of candidates) {
      for (const field of BODY_TEXT_FIELDS) {
        if (!(field in node)) continue
        const raw = clean(extractText(node[field]))
        // Require a real sentence, not a one-word label or a stray price.
        if (raw && raw.split(' ').length >= 6) {
          return truncate(raw)
        }
      }
    }
  }
  return ''
}

/**
 * Derive a distinct, descriptive <title> (the part before the `| VRS` template).
 * - Uses the page title.
 * - For product checkout pages, appends a short "Buy …" qualifier so the buy
 *   page does not collide with the hardware overview page of the same product.
 * - Falls back to a slug-derived title if the doc has none.
 */
// The homepage's CMS title is the generic "Home" (→ "Home | VRS"), which wastes
// the single most valuable <title> on the site. Special-case the root slug to a
// keyword-rich homepage title (the region variants /us,/eu,/au all render the
// home route with an empty bare slug, so they share it). (#147)
export const HOME_TITLE = 'Sim Racing Wheelbases, Pedals & iRacing Coaching'

export const deriveTitle = ({ title, content = [], tag, slug, seoTitle } = {}) => {
  // Explicit per-page SEO <title> override (Wave-1 striking-distance capture,
  // #158). Lets a page set a keyword-rich <title> WITHOUT changing the CMS
  // `title`, which also feeds nav/breadcrumb labels + the H1 fallback. Wins over
  // everything below (the page-level template still appends "| VRS").
  if (typeof seoTitle === 'string' && seoTitle.trim()) return seoTitle.trim()
  // Root slug (home + its /us,/eu,/au region variants) -> keyword-rich title,
  // ignoring the generic CMS "Home". The home route passes an EXPLICIT '/' (or '')
  // slug (see app/[[...slug]]/page.js); a missing slug means "unspecified" and is
  // left to the normal title path.
  if (typeof slug === 'string' && slug.replace(/^\/+|\/+$/g, '') === '') {
    return HOME_TITLE
  }
  let base = (typeof title === 'string' && title.trim()) || titleFromSlug(slug)
  if (!base) return 'VRS Racing'
  // Strip a brand suffix already baked into the doc title — the page-level
  // titleTemplate ("%s | VRS") re-appends the brand, so otherwise a doc titled
  // "… | Virtual Racing School" becomes "… | Virtual Racing School | VRS"
  // (double brand + an over-long title, e.g. the hardware overview page).
  base = base.replace(/\s*\|\s*(Virtual Racing School|VRS)\s*$/i, '').trim()
  if (isCheckoutPage(content, tag) && !/buy/i.test(base)) {
    return `Buy ${base}`
  }
  return base
}

// Humanise the last slug segment as a last-resort title.
const titleFromSlug = (slug) => {
  if (!slug || slug === '/') return ''
  const last = String(slug).split('/').filter(Boolean).pop() || ''
  return last
    .split('-')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ')
}

/**
 * The single <h1> text for the page (the template renders exactly one).
 * Always the human title, never region-prefixed.
 */
export const deriveH1 = ({ title, slug } = {}) =>
  (typeof title === 'string' && title.trim()) || titleFromSlug(slug) || 'VRS Racing'
